import { useParams, Link } from "react-router-dom";
import { siteConfig } from "@/mocks/siteData";
import { articleDetails, categoryArticles } from "@/mocks/categoryData";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

function findArticleById(id: number) {
  if (articleDetails[id]) return articleDetails[id];

  for (const slug of Object.keys(categoryArticles)) {
    const found = categoryArticles[slug].find((a) => a.id === id);
    if (found) {
      return {
        id: found.id,
        title: found.title,
        category: slug,
        categoryName: found.author.includes("小艾") ? "恋爱技巧" : found.author.includes("小鹿") ? "情感故事" : found.author.includes("李老师") ? "婚姻经营" : found.author.includes("王教授") ? "心理学堂" : found.author.includes("陈老师") ? "两性话题" : "热门综艺",
        author: found.author,
        authorAvatar: "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20warm%20smiling%20Chinese%20person%2C%20clean%20white%20background%2C%20professional%20yet%20friendly%20look&width=100&height=100&seq=author-default&orientation=squarish",
        date: found.date,
        views: found.views,
        duration: found.duration,
        thumbnail: found.thumbnail,
        tags: found.tags,
        summary: found.description,
        content: [
          { type: "heading" as const, text: "内容简介" },
          { type: "paragraph" as const, text: found.description },
          { type: "paragraph" as const, text: `本视频由${found.author}倾情呈现，为大家带来关于${found.title}的精彩内容。更多详细内容正在制作中，敬请期待完整版上线。` },
        ],
        relatedArticles: [],
      };
    }
  }
  return null;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = findArticleById(articleId);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 mb-4">未找到相关内容</p>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
      </main>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    author: {
      "@type": "Person",
      name: article.author,
    },
    datePublished: article.date,
    image: article.thumbnail,
    publisher: {
      "@type": "Organization",
      name: siteConfig.siteName,
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.siteUrl },
        { "@type": "ListItem", position: 2, name: article.categoryName, item: `${siteConfig.siteUrl}/category/${article.category}` },
        { "@type": "ListItem", position: 3, name: article.title, item: `${siteConfig.siteUrl}/article/${article.id}` },
      ],
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteConfig.siteUrl}/article/${article.id}`,
    },
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - ${siteConfig.siteName}`}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonicalUrl={`${siteConfig.siteUrl}/article/${article.id}`}
        ogTitle={`${article.title} - ${siteConfig.siteName}`}
        ogDescription={article.summary}
        ogType="article"
        ogImage={article.thumbnail}
        ogUrl={`${siteConfig.siteUrl}/article/${article.id}`}
        jsonLd={[articleSchema]}
      />

      <main className="min-h-screen bg-background-50 pt-16">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <article>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 flex-wrap text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-background-200">
                    <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-foreground-700 font-medium">{article.author}</span>
                </div>
                <span>{article.date}</span>
                <span><i className="ri-eye-line mr-1"></i>{formatViews(article.views)}</span>
                <span><i className="ri-time-line mr-1"></i>{article.duration}</span>
              </div>
            </div>

            <div className="relative rounded-lg overflow-hidden aspect-video mb-8">
              <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600 transition-colors">
                  <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                </div>
              </div>
            </div>

            <div className="max-w-none">
              {article.content.map((block, index) => {
                if (block.type === "heading" && block.text) {
                  return (
                    <h3 key={index} className="text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-3">
                      {block.text}
                    </h3>
                  );
                }
                if (block.type === "paragraph" && block.text) {
                  return (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "list" && block.items) {
                  return (
                    <ul key={index} className="list-disc pl-5 mb-4 space-y-1.5">
                      {block.items.map((item, i) => (
                        <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">{item}</li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享到：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:text-primary-500 hover:bg-primary-100/70 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>
        </div>
      </main>
    </>
  );
}